import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BookOpen, Globe, Images, Lightbulb, Menu, Rocket, Tag, Trophy, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import type { Bilingual } from '../i18n/LanguageContext';
import { copy } from '../i18n/copy';
import { missions } from '../data/missions';
import type { MissionId } from '../data/missions';
import { scrollToId } from '../utils/scroll';

interface HeaderProps {
  onSelectMission: (id: MissionId) => void;
}

const missionIcons: Record<MissionId, LucideIcon> = {
  learn: BookOpen,
  compete: Trophy,
  prototype: Lightbulb,
};

export default function Header({ onSelectMission }: HeaderProps) {
  const { t, lang, toggle } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navItems: { key: string; label: Bilingual; icon: LucideIcon; action: () => void }[] = [
    ...missions.map((m) => ({
      key: m.id as string,
      label: m.navLabel,
      icon: missionIcons[m.id],
      action: () => onSelectMission(m.id),
    })),
    { key: 'gallery', label: copy.navGallery, icon: Images, action: () => scrollToId('projects') },
    { key: 'pricing', label: copy.navPricing, icon: Tag, action: () => scrollToId('pricing') },
  ];

  const handleNav = (action: () => void) => {
    setMenuOpen(false);
    action();
  };

  return (
    <header
      className={`sticky top-0 z-40 border-b backdrop-blur-xl transition-colors duration-300 ${
        scrolled ? 'border-white/10 bg-navy-950/70' : 'border-transparent bg-navy-950/30'
      }`}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-5">
        {/* Official logo on a light chip so the navy wordmark stays readable */}
        <a
          href="#"
          className="flex shrink-0 items-center"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          aria-label="Taa Sandbox — back to top"
        >
          <span className="inline-flex items-center rounded-xl bg-white px-2.5 py-1 shadow-[0_0_25px_-8px_rgba(56,189,248,0.6)]">
            <img src="/assets/logo.png" alt="Taa Sandbox logo" className="h-8 w-auto" />
          </span>
        </a>

        {/* Center nav (desktop) — pill container with icons */}
        <nav className="hidden items-center gap-1 rounded-2xl border border-white/10 bg-white/[0.04] p-1.5 md:flex">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handleNav(item.action)}
                className="inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-semibold text-slate-300 transition-colors hover:bg-white/5 hover:text-white"
              >
                <Icon className="size-4 text-sky-400" />
                {t(item.label)}
              </button>
            );
          })}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggle}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-slate-300 ring-1 ring-white/10 transition-colors hover:text-white hover:ring-white/25"
          >
            <Globe className="size-4" />
            {lang === 'en' ? 'عربي' : 'English'}
          </button>

          <motion.a
            href="/src/client/pages/dashboard.html"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            className="hidden items-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-2.5 text-sm font-bold text-navy-950 shadow-[0_0_30px_-8px_rgba(251,146,60,0.7)] sm:inline-flex"
          >
            <Rocket className="size-4" />
            {t(copy.startBuilding)}
          </motion.a>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            className="grid size-10 place-items-center rounded-lg text-slate-200 ring-1 ring-white/10 md:hidden"
          >
            {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-white/10 bg-navy-950/90 backdrop-blur-xl md:hidden"
          >
            <div className="space-y-1 px-5 py-4">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => handleNav(item.action)}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-start text-sm font-medium text-slate-200 hover:bg-white/5"
                  >
                    <Icon className="size-4 text-sky-400" />
                    {t(item.label)}
                  </button>
                );
              })}
              <a
                href="/src/client/pages/dashboard.html"
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-3 text-sm font-bold text-navy-950"
              >
                <Rocket className="size-4" />
                {t(copy.startBuilding)}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
